import React, { useEffect } from 'react';
import { useInvoiceSummary } from '../../hooks/useInvoiceSummary';
import './../../scss/components/stat-card.style.scss';
import { useTranslation } from 'react-i18next';
import { useUserDashboard } from '../../hooks/useUserDashboard';

interface StatCardProps {
  title: string;
  children?: React.ReactNode;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  children,
  className = ''
}) => {
  const {
    loading,
    error,
    hasData,
    totalAmount,
    paidAmount,
    overdueAmount,
    formatAmount,
    paidPercentage,
    overduePercentage,
    fetchSummary
  } = useInvoiceSummary();
  const {
    dashboardData,
  } = useUserDashboard();
  const { t } = useTranslation();

  // Fetch summary data on component mount
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Loading state
  if (loading) {
    return (
      <div className={`stat-card ${className}`}>
        <div className="stat-card-header">
          <h3 className="stat-card-title">{title}</h3>
          {children}
        </div>
        <div className="stat-card-body d-flex justify-content-between align-items-center">
          <div className="loading-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
          }}>
            Loading summary...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`stat-card ${className}`}>
        <div className="stat-card-header">
          <h3 className="stat-card-title">{title}</h3>
          {children}
        </div>
        <div className="stat-card-body">
          <div className="error-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#dc3545'
          }}>
            Error loading summary: {error}
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!hasData) {
    return (
      <div className={`stat-card ${className}`}>
        <div className="stat-card-header">
          <h3 className="stat-card-title">{title}</h3>
          {children}
        </div>
        <div className="stat-card-body">
          <div className="empty-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
          }}>
            No summary data available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`stat-card ${className}`}>
      <div className="stat-card-header">
        <div>

          <h3 className="stat-card-title">{title}</h3>
         {Number(dashboardData?.response?.length) === 0 && <p className='d-none d-md-block'>{t('NO_RECEIVABLES_MESSAGE')}</p>}
        </div>
        {children}

      </div>

      <div className="stat-card-body">
        <div className="stat-total-section mb-0">
          <p className="stat-total-label">{t('TOTAL_INVOICES')} : {formatAmount(totalAmount)} { t('kr')}</p>
          <div className="stat-progress-bar">
            {(paidPercentage > 0 || overduePercentage > 0) ? (
              <>
                <div
                  className="stat-progress-paid"
                  style={{ width: `${paidPercentage}%` }}
                ></div>
                <div
                  className="stat-progress-overdue"
                  style={{ width: `${overduePercentage}%` }}
                ></div>
              </>
            ) : (
              <div
                className="stat-progress-empty"
                style={{ width: '100%', backgroundColor: '#C2BDDE' }}
              ></div>
            )}
          </div>
        </div>

        <div className="stat-values-section">
          <div className="stat-divider d-md-block d-none"></div>
          <div className="stat-value-item">  
            <p className="stat-value-label paid">{t('PAID')}</p>
            <p className="stat-value-amount">{formatAmount(paidAmount)} { t('kr')}</p>
          </div>

          {/* <div className="stat-divider"></div>
          <div className="stat-value-item">
            <p className="stat-value-label overdue">{t('OVERDUE')}</p>
            <p className="stat-value-amount">{formatAmount(overdueAmount)}</p>
          </div> */}
        </div>
      </div>
    </div>
  );
};
